import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useEventSelection } from '../../../contexts/useEventSelection';
import sessionsService, { type Session } from '../../../api/sessionsService';
import type { ObservationAvailability } from '../types';
import type { StationData } from '../../../api/stationObservationsService';
import { isTioProvider, type MapTileProvider } from '../../../pages/Nowcasting/useNowcastingState';

dayjs.extend(utc);
dayjs.extend(timezone);

export interface UseTimelineDataParams {
  stationsData?: StationData[];
  mode: 'range' | 'point';
  liveStartMinutes: number;
  liveEndMinutes: number;
  liveEndMinutesForRadar: number;
  mapTileProvider: MapTileProvider;
  liveStartMinLimit: number;
  onDateRangeChange?: (startDate: Date, endDate: Date) => void;
  onDatePointChange?: (date: Date) => void;
  onLiveModeChange?: (isLive: boolean) => void;
}

export interface UseTimelineDataReturn {
  sessions: Session[];
  observationAvailability: ObservationAvailability | null;
  loading: boolean;
  error: string | null;
  selectedRange: { start: Date; end: Date } | null;
  setSelectedRange: React.Dispatch<React.SetStateAction<{ start: Date; end: Date } | null>>;
  selectedPoint: Date | null;
  setSelectedPoint: React.Dispatch<React.SetStateAction<Date | null>>;
  isLiveMode: boolean;
  setIsLiveMode: React.Dispatch<React.SetStateAction<boolean>>;
  currentTime: Date;
  pendingStartMinutes: number | null;
  setPendingStartMinutes: React.Dispatch<React.SetStateAction<number | null>>;
  pendingEndMinutes: number | null;
  setPendingEndMinutes: React.Dispatch<React.SetStateAction<number | null>>;
  canvasRenderKey: number;
  setCanvasRenderKey: React.Dispatch<React.SetStateAction<number>>;
}

export function useTimelineData(params: UseTimelineDataParams): UseTimelineDataReturn {
  const { selectedEvent } = useEventSelection();
  const {
    stationsData,
    mode,
    liveStartMinutes,
    liveEndMinutes,
    liveEndMinutesForRadar,
    mapTileProvider,
    liveStartMinLimit,
    onDateRangeChange,
    onDatePointChange,
    onLiveModeChange,
  } = params;

  const [sessions, setSessions] = useState<Session[]>([]);
  const [observationAvailability, setObservationAvailability] = useState<ObservationAvailability | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRange, setSelectedRange] = useState<{ start: Date; end: Date } | null>(null);
  const [selectedPoint, setSelectedPoint] = useState<Date | null>(null);
  const [isLiveMode, setIsLiveMode] = useState(false);
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [pendingStartMinutes, setPendingStartMinutes] = useState<number | null>(liveStartMinutes);
  const [pendingEndMinutes, setPendingEndMinutes] = useState<number | null>(liveEndMinutes);
  const [canvasRenderKey, setCanvasRenderKey] = useState(0);

  const hasInitializedRef = useRef(false);
  const lastEventKeyRef = useRef<string>('');
  const liveIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const liveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timeUpdateIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Sync local state when props change
  useEffect(() => {
    setPendingStartMinutes(liveStartMinutes);
    setPendingEndMinutes(liveEndMinutes);
  }, [liveStartMinutes, liveEndMinutes]);

  // Notify parent when live mode changes (for PlaybackControls dock buttons visibility)
  useEffect(() => {
    onLiveModeChange?.(isLiveMode);
  }, [isLiveMode, onLiveModeChange]);

  // Effect to handle visibility change and window focus - force canvas re-render only
  useEffect(() => {
    const handleVisibilityOrFocus = () => {
      setCanvasRenderKey((prev) => prev + 1);
      setCurrentTime(new Date());
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleVisibilityOrFocus();
      }
    };

    const handleWindowFocus = () => {
      handleVisibilityOrFocus();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, []);

  // Effect to update currentTime every minute for the "NOW" line indicator
  useEffect(() => {
    const now = new Date();
    const msUntilNextMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds();

    const alignTimeout = setTimeout(() => {
      setCurrentTime(new Date());
      timeUpdateIntervalRef.current = setInterval(() => {
        setCurrentTime(new Date());
      }, 60 * 1000);
    }, msUntilNextMinute);

    return () => {
      clearTimeout(alignTimeout);
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current);
        timeUpdateIntervalRef.current = null;
      }
    };
  }, []);

  // Live mode effect: auto-update selected time every minute when tracking current time
  useEffect(() => {
    if (liveTimeoutRef.current) {
      clearTimeout(liveTimeoutRef.current);
      liveTimeoutRef.current = null;
    }
    if (liveIntervalRef.current) {
      clearInterval(liveIntervalRef.current);
      liveIntervalRef.current = null;
    }

    if (!isLiveMode || !selectedEvent) return;

    const updateToCurrentTime = () => {
      const now = new Date();

      if (mode === 'point') {
        setSelectedPoint(now);
        onDatePointChange?.(now);
      } else {
        const startMinutes = Math.max(liveStartMinLimit, Math.min(0, liveStartMinutes));
        const endMinutes =
          isTioProvider(mapTileProvider)
            ? Math.max(1, Math.min(60, liveEndMinutes))
            : Math.max(0, Math.min(60, liveEndMinutesForRadar));
        const start = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          now.getHours(),
          now.getMinutes() + startMinutes,
          0,
          0
        );
        const end = new Date(now.getTime() + endMinutes * 60 * 1000);
        setSelectedRange({ start, end });
        onDateRangeChange?.(start, end);
      }
    };

    const now = new Date();
    const msUntilNextMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds();

    // Update immediately when effect runs (e.g. switching to t.io, live minutes change)
    // so timeline range and t.io forecast panel sync right away instead of waiting for next minute
    updateToCurrentTime();

    liveTimeoutRef.current = setTimeout(() => {
      updateToCurrentTime();
      liveIntervalRef.current = setInterval(updateToCurrentTime, 60 * 1000);
    }, msUntilNextMinute);

    return () => {
      if (liveTimeoutRef.current) {
        clearTimeout(liveTimeoutRef.current);
        liveTimeoutRef.current = null;
      }
      if (liveIntervalRef.current) {
        clearInterval(liveIntervalRef.current);
        liveIntervalRef.current = null;
      }
    };
  }, [
    isLiveMode,
    selectedEvent,
    mode,
    onDatePointChange,
    onDateRangeChange,
    liveStartMinutes,
    liveEndMinutes,
    liveEndMinutesForRadar,
    mapTileProvider,
    liveStartMinLimit,
  ]);

  // Fetch sessions and set initial selection range
  useEffect(() => {
    if (!selectedEvent) {
      hasInitializedRef.current = false;
      lastEventKeyRef.current = '';
      setIsLiveMode(false);
      return;
    }

    const eventKey = `${selectedEvent.season_year}-${selectedEvent.circuit_id}-${selectedEvent.start_date}`;

    if (lastEventKeyRef.current !== eventKey) {
      hasInitializedRef.current = false;
      lastEventKeyRef.current = eventKey;
      setIsLiveMode(false);
    }

    const fetchSessions = async () => {
      setLoading(true);
      setError(null);

      try {
        const sessionsData = await sessionsService.getSessionsByEvent(
          selectedEvent.season_year,
          selectedEvent.circuit_id,
          selectedEvent.start_date
        );
        setSessions(sessionsData);

        if (stationsData && stationsData.length > 0) {
          let earliestTime: Date | null = null;
          let latestTime: Date | null = null;

          stationsData.forEach((station) => {
            station.observations.forEach((obs) => {
              const timestamp = new Date(obs.reading_timestamp);
              if (!earliestTime || timestamp < earliestTime) {
                earliestTime = timestamp;
              }
              if (!latestTime || timestamp > latestTime) {
                latestTime = timestamp;
              }
            });
          });

          if (earliestTime && latestTime) {
            setObservationAvailability({ start: earliestTime, end: latestTime });
          }
        } else {
          const tz = selectedEvent.circuit?.time_zone || 'UTC';
          const eventStart = dayjs.tz(selectedEvent.start_date, tz).startOf('day').toDate();
          const eventEnd = dayjs.tz(selectedEvent.end_date, tz).endOf('day').toDate();
          setObservationAvailability({ start: eventStart, end: eventEnd });
        }

        if (!hasInitializedRef.current) {
          const tz = selectedEvent.circuit?.time_zone || 'UTC';
          const now = new Date();
          const todayInCircuitTz = dayjs().tz(tz).format('YYYY-MM-DD');
          const isEventActive =
            todayInCircuitTz >= selectedEvent.start_date &&
            todayInCircuitTz <= selectedEvent.end_date;

          let initialStart: Date;
          let initialEnd: Date;

          if (isEventActive) {
            const startMinutes = Math.max(liveStartMinLimit, Math.min(0, liveStartMinutes));
            const endMinutes =
              isTioProvider(mapTileProvider)
                ? Math.max(1, Math.min(60, liveEndMinutes))
                : Math.max(0, Math.min(60, liveEndMinutesForRadar));
            initialStart = new Date(
              now.getFullYear(),
              now.getMonth(),
              now.getDate(),
              now.getHours(),
              now.getMinutes() + startMinutes,
              0,
              0
            );
            initialEnd = new Date(now.getTime() + endMinutes * 60 * 1000);
          } else {
            if (sessionsData.length > 0) {
              initialStart = new Date(sessionsData[0].start_time);
              initialEnd = new Date(sessionsData[0].end_time);
            } else {
              const eventDateStr = selectedEvent.start_date;
              initialStart = dayjs
                .tz(`${eventDateStr} 10:00:00`, 'YYYY-MM-DD HH:mm:ss', tz)
                .toDate();
              initialEnd = dayjs
                .tz(`${eventDateStr} 12:00:00`, 'YYYY-MM-DD HH:mm:ss', tz)
                .toDate();
            }
          }

          if (mode === 'range') {
            setSelectedRange({ start: initialStart, end: initialEnd });
            onDateRangeChange?.(initialStart, initialEnd);
          } else {
            setSelectedPoint(initialStart);
            onDatePointChange?.(initialStart);
          }

          if (isEventActive) {
            setIsLiveMode(true);
          }
          hasInitializedRef.current = true;
        }
      } catch (err) {
        console.error('Error fetching timeline data:', err);
        setError('Failed to load timeline data');
      } finally {
        setLoading(false);
      }
    };

    fetchSessions();
  }, [
    selectedEvent,
    stationsData,
    onDateRangeChange,
    onDatePointChange,
    mode,
    liveStartMinutes,
    liveEndMinutes,
    liveEndMinutesForRadar,
    mapTileProvider,
    liveStartMinLimit,
  ]);

  return {
    sessions,
    observationAvailability,
    loading,
    error,
    selectedRange,
    setSelectedRange,
    selectedPoint,
    setSelectedPoint,
    isLiveMode,
    setIsLiveMode,
    currentTime,
    pendingStartMinutes,
    setPendingStartMinutes,
    pendingEndMinutes,
    setPendingEndMinutes,
    canvasRenderKey,
    setCanvasRenderKey,
  };
}
